import { Classes, Dialog } from "@blueprintjs/core";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import {
    DefaultSelectedOption,
    SimpleSelect,
    filterItemPredicate,
    renderSingleOptionFactory,
} from "modules/common/services/form/select";
import { useMemo, useState } from "react";
import { MailAgentOption, MailOrderOption } from "./mail-format";
import styles from "./AgentMailbox.module.scss";

const renderSingleOption = renderSingleOptionFactory(() => null);

interface AttachDialogProps {
    /** Письма с незнакомого адреса: нужно выбрать и агента */
    needAgent: boolean;
    senderEmail: string | null;
    currentOrder: MailOrderOption | null;
    orders: MailOrderOption[];
    agents: MailAgentOption[];
    onSave: (order: MailOrderOption | null, agent: string | null) => Promise<boolean>;
    onClose: () => void;
}

export const AttachDialog = ({
    needAgent,
    senderEmail,
    currentOrder,
    orders,
    agents,
    onSave,
    onClose,
}: AttachDialogProps) => {
    const [order, setOrder] = useState<MailOrderOption | null>(currentOrder);
    const [agent, setAgent] = useState<SelectItem | null>(null);
    const [saving, setSaving] = useState(false);

    const agentItems = useMemo(
        () =>
            agents
                .filter((a) => a.isAgent)
                .map((a) => ({ id: a.id, label: `${a.name}${a.email ? ` <${a.email}>` : ""}` })),
        [agents]
    );

    const save = async () => {
        setSaving(true);
        const ok = await onSave(order, agent?.id ?? null);
        setSaving(false);

        if (ok) {
            onClose();
        }
    };

    return (
        <Dialog
            isOpen={true}
            title="Привязать переписку"
            backdropClassName="standard"
            onClose={onClose}
            className="figma-dialog"
            style={{ width: 520 }}
        >
            <div className={Classes.DIALOG_BODY}>
                {needAgent && (
                    <div className={styles.field}>
                        <div className={styles.label}>
                            Агент{senderEmail ? ` (письмо с адреса ${senderEmail})` : ""}
                        </div>
                        <SimpleSelect
                            className={`full-width-select ${Classes.FILL}`}
                            activeItem={agent}
                            items={agentItems}
                            itemRenderer={renderSingleOption}
                            onItemSelect={(item: SelectItem) => setAgent(item)}
                            popoverProps={{ rootBoundary: "viewport" }}
                            inputProps={{ placeholder: "Поиск" }}
                            filterable={true}
                            itemPredicate={filterItemPredicate}
                        >
                            <DefaultSelectedOption option={agent} what="Не выбран" onClear={() => setAgent(null)} />
                        </SimpleSelect>
                    </div>
                )}
                <div className={styles.field}>
                    <div className={styles.label}>Договор</div>
                    <SimpleSelect
                        className={`full-width-select ${Classes.FILL}`}
                        activeItem={order}
                        items={orders}
                        itemRenderer={renderSingleOption}
                        onItemSelect={(item: SelectItem) => setOrder(item)}
                        popoverProps={{ rootBoundary: "viewport" }}
                        inputProps={{ placeholder: "Поиск" }}
                        filterable={true}
                        itemPredicate={filterItemPredicate}
                    >
                        <DefaultSelectedOption option={order} what="Без договора" onClear={() => setOrder(null)} />
                    </SimpleSelect>
                </div>
            </div>
            <div className={Classes.DIALOG_FOOTER}>
                <div className={styles.actions}>
                    <PlanrButton
                        type="greenish"
                        size="small"
                        onClick={save}
                        disabled={saving || (needAgent && !agent && !order)}
                    >
                        Сохранить
                    </PlanrButton>
                    <PlanrButton type="graybtn" size="small" onClick={onClose}>
                        Отмена
                    </PlanrButton>
                </div>
            </div>
        </Dialog>
    );
};
